// src/models/TimeEntryReportModel.js

/**
 * Sessions shorter than this (seconds) are treated as accidental taps and
 * hidden in reports (All Time Entries rows, Working Time Record start/end).
 */
export const MIN_SESSION_SECONDS = 60;

/**
 * Model representing a time entry report item
 */
export class TimeEntryReport {
  /**
   * Create a new TimeEntryReport
   * @param {string} id - Unique identifier for this report entry
   * @param {string} staffId - Staff member ID
   * @param {string} staffName - Staff member name
   * @param {Date} date - Date of the entry (day only)
   * @param {Date} clockIn - Clock in timestamp
   * @param {Date} clockOut - Clock out timestamp
   * @param {number} breakDuration - Total break duration in seconds
   * @param {number} presenceDuration - Total presence duration in seconds (clock in to clock out)
   * @param {string} [sessionId] - Optional session identifier
   * @param {string} [departmentId] - Optional department identifier
   * @param {string} [departmentName] - Optional department name
   */
  constructor(
    id,
    staffId,
    staffName,
    date,
    clockIn,
    clockOut,
    breakDuration,
    presenceDuration,
    sessionId = null,
    departmentId = null,
    departmentName = null
  ) {
    this.id = id;
    this.staffId = staffId;
    this.staffName = staffName;
    this.date = date;
    this.clockIn = clockIn;
    this.clockOut = clockOut;
    this.breakDuration = Number(breakDuration) || 0;
    this.presenceDuration = Number(presenceDuration) || 0;
    this.sessionId = sessionId;
    this.departmentId = departmentId;
    this.departmentName = departmentName;
  }

  /**
   * Get the actual work duration (paid time) - presence minus breaks
   * @returns {number} Work duration in seconds
   */
  get workDuration() {
    return Math.max(0, this.presenceDuration - this.breakDuration);
  }

  /**
   * Get the total presence duration (total time at workplace)
   * @returns {number} Total presence duration in seconds
   */
  get totalDuration() {
    return this.presenceDuration;
  }

  /**
   * Create a TimeEntryReport from Firestore data (used by Daily Summary).
   *
   * presenceDuration is computed as the sum of raw clock-in/clock-out spans so
   * that multi-session days are handled correctly.
   *
   * breakDuration is taken from totalBreakDuration, which includes any break
   * time added by the nightly statutory-compliance job — making workDuration
   * (presence − totalBreakDuration) the canonical net work figure (Arbeitszeit).
   *
   * NOTE: totalWorkDuration is intentionally NOT used here. Both iOS
   * (TimeTrackingViewModel L792) and web
   * (TimeManagementService.calculateSessionDurations L247) persist
   * totalWorkDuration as net work time (Arbeitszeit = presence − breaks).
   * Reading it to derive presence would therefore under-count by the full break
   * duration and must not be done.
   *
   * @param {Object} data - Raw Firestore data
   * @param {string} entryId - Entry document ID
   * @param {string} staffId - Staff ID
   * @param {string} staffName - Staff name
   * @returns {TimeEntryReport} A new TimeEntryReport instance
   */
  static fromFirestore(data, entryId, staffId, staffName) {
    // Convert date string to Date object
    const dateComponents = data.date.split('-').map(Number);
    const entryDate = new Date(dateComponents[0], dateComponents[1] - 1, dateComponents[2]);

    const clockIns = data.clockIns || [];
    const clockOuts = data.clockOuts || [];

    const firstClockIn = clockIns.length > 0
      ? new Date((clockIns[0]?.seconds || 0) * 1000)
      : entryDate;

    const lastClockOut = clockOuts.length > 0
      ? new Date((clockOuts[clockOuts.length - 1]?.seconds || 0) * 1000)
      : entryDate;

    // breakDuration comes from totalBreakDuration so it includes any break time
    // added by the nightly compliance job. This is what makes workDuration the
    // correct canonical net work figure used by Daily Summary.
    const breakDuration = Number(data.totalBreakDuration) || 0;

    // Compute presenceDuration (Anwesenheit) as the sum of raw clock-in/out
    // spans across all sessions in this day entry.
    const sessionCount = Math.min(clockIns.length, clockOuts.length);
    let rawPresenceDuration = 0;

    for (let i = 0; i < sessionCount; i++) {
      const ciMs = (clockIns[i]?.seconds || 0) * 1000;
      const coMs = (clockOuts[i]?.seconds || 0) * 1000;
      rawPresenceDuration += Math.max(0, (coMs - ciMs) / 1000);
    }

    const presenceDuration = rawPresenceDuration > 0 ? rawPresenceDuration : 0;

    return new TimeEntryReport(
      entryId,
      staffId,
      staffName,
      entryDate,
      firstClockIn,
      lastClockOut,
      breakDuration,
      presenceDuration,
      null,
      data.departmentId,
      null
    );
  }

  /**
   * Create one TimeEntryReport per day for the Working Time Record report.
   *
   * Durations are identical to fromFirestore (Daily Summary):
   *  - presenceDuration = sum of ALL completed clock-in/out spans
   *  - breakDuration    = totalBreakDuration (includes compliance-added time)
   *  - workDuration     = presence − break (getter)
   *
   * Sessions shorter than MIN_SESSION_SECONDS (accidental taps) are ignored
   * only for the displayed first clock-in / last clock-out. Their few seconds
   * stay in presenceDuration so the day totals match Daily Summary exactly.
   *
   * Returns null if the day has no valid (>= MIN_SESSION_SECONDS) session, so
   * the day is hidden from the report.
   *
   * @param {Object} data - Raw Firestore data
   * @param {string} entryId - Entry document ID
   * @param {string} staffId - Staff ID
   * @param {string} staffName - Staff name
   * @returns {TimeEntryReport|null} A new TimeEntryReport instance or null
   */
  static dayFromFirestore(data, entryId, staffId, staffName) {
    // Convert date string to Date object
    const dateComponents = data.date.split('-').map(Number);
    const entryDate = new Date(dateComponents[0], dateComponents[1] - 1, dateComponents[2]);

    const clockIns = data.clockIns || [];
    const clockOuts = data.clockOuts || [];
    const sessionCount = Math.min(clockIns.length, clockOuts.length);

    let presenceDuration = 0;
    let firstInMs = null;
    let lastOutMs = null;

    for (let i = 0; i < sessionCount; i++) {
      const ciMs = (clockIns[i]?.seconds || 0) * 1000;
      const coMs = (clockOuts[i]?.seconds || 0) * 1000;
      const span = Math.max(0, (coMs - ciMs) / 1000);

      // Presence always includes every completed session (matches Daily Summary).
      presenceDuration += span;

      // Accidental short taps must not define the displayed start / end time.
      if (span < MIN_SESSION_SECONDS) continue;

      if (firstInMs === null || ciMs < firstInMs) firstInMs = ciMs;
      if (lastOutMs === null || coMs > lastOutMs) lastOutMs = coMs;
    }

    // No valid session left: hide this day.
    if (firstInMs === null) return null;

    return new TimeEntryReport(
      entryId,
      staffId,
      staffName,
      entryDate,
      new Date(firstInMs),
      new Date(lastOutMs),
      Number(data.totalBreakDuration) || 0,
      presenceDuration,
      null,
      data.departmentId,
      null
    );
  }

  /**
   * Create multiple TimeEntryReport instances, one per session (used by All
   * Time Entries).
   *
   * Each row surfaces the raw presence span (clockOut − clockIn) for that
   * session. This matches the Start/End columns shown in the report and makes
   * the per-row and monthly totals equal the Daily Summary Anwesenheit figure.
   *
   * Sessions shorter than MIN_SESSION_SECONDS (accidental taps) are skipped, so
   * the monthly total can be a few seconds lower than Daily Summary.
   *
   * breakDuration is still computed from the breakStarts/breakEnds arrays and
   * stored on each session so that workDuration remains accessible if needed.
   * IMPORTANT: these arrays only contain explicitly logged breaks. The nightly
   * compliance job bumps totalBreakDuration only — it never writes to the
   * arrays — so per-session breakDuration derived here may be lower than the
   * compliance-adjusted value stored in totalBreakDuration for the full day.
   *
   * @param {Object} data - Raw Firestore data
   * @param {string} entryId - Entry document ID
   * @param {string} staffId - Staff ID
   * @param {string} staffName - Staff name
   * @returns {TimeEntryReport[]} Array of TimeEntryReport instances
   */
  static sessionsFromFirestore(data, entryId, staffId, staffName) {
    // Convert date string to Date object
    const dateComponents = data.date.split('-').map(Number);
    const entryDate = new Date(dateComponents[0], dateComponents[1] - 1, dateComponents[2]);

    const clockIns = data.clockIns || [];
    const clockOuts = data.clockOuts || [];
    const breakStarts = data.breakStarts || [];
    const breakEnds = data.breakEnds || [];

    const sessions = [];
    const sessionCount = Math.min(clockIns.length, clockOuts.length);

    for (let i = 0; i < sessionCount; i++) {
      const sessionClockIn = new Date((clockIns[i]?.seconds || 0) * 1000);
      const sessionClockOut = new Date((clockOuts[i]?.seconds || 0) * 1000);

      // Presence duration for this session is the raw span from timestamps.
      // The All Time Entries report surfaces this value so each row ties
      // exactly to the displayed Start/End columns.
      const sessionPresenceDuration = Math.max(
        0,
        (sessionClockOut.getTime() - sessionClockIn.getTime()) / 1000
      );

      // Hide accidental short taps (e.g. 07:51 - 07:51).
      if (sessionPresenceDuration < MIN_SESSION_SECONDS) continue;

      // Compute break duration from the breakStarts/breakEnds arrays for breaks
      // that fall within this session window. Note: compliance-added break time
      // (reflected in totalBreakDuration) is NOT captured here because the
      // compliance job does not write to these arrays.
      let sessionBreakDuration = 0;
      const breakPairCount = Math.min(breakStarts.length, breakEnds.length);

      for (let j = 0; j < breakPairCount; j++) {
        const bsMs = (breakStarts[j]?.seconds || 0) * 1000;
        const beMs = (breakEnds[j]?.seconds || 0) * 1000;
        const breakStart = new Date(bsMs);
        const breakEnd = new Date(beMs);

        if (
          breakStart >= sessionClockIn &&
          breakEnd <= sessionClockOut &&
          breakEnd > breakStart
        ) {
          sessionBreakDuration += (beMs - bsMs) / 1000;
        }
      }

      sessions.push(
        new TimeEntryReport(
          `${entryId}-session-${i + 1}`,
          staffId,
          staffName,
          entryDate,
          sessionClockIn,
          sessionClockOut,
          sessionBreakDuration,
          sessionPresenceDuration,
          `${i + 1}`,
          data.departmentId,
          null
        )
      );
    }

    return sessions;
  }
}

/**
 * Format a duration in seconds to a human-readable string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string (e.g. "2h 30m")
 */
export function formatDuration(seconds) {
  if (isNaN(seconds) || seconds === null || seconds === undefined) {
    return '0h 0m';
  }

  const roundedSeconds = Math.round(Number(seconds));
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

/**
 * Format a duration in seconds to a decimal hours string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string (e.g. "2.50")
 */
export function formatDurationNumeric(seconds) {
  if (isNaN(seconds) || seconds === null || seconds === undefined) {
    return '0.00';
  }
  const hours = Number(seconds) / 3600;
  return hours.toFixed(2);
}

/**
 * Format a date for display
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date).replace(/\./g, '.');
}

/**
 * Format a date for use in filenames and Firestore queries
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string (YYYY-MM-DD) in local timezone
 */
export function formatFileDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a time for display — always 24h (German format), e.g. "07:50".
 * @param {Date} date - Date/time to format
 * @returns {string} Formatted time string
 */
export function formatTime(date) {
  return new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
}